import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';
import { usePost, get_deployment } from '../API';

// Export types
export type ExportFormat = 'csv' | 'xlsx';

export type ExportJobStatus = {
  taskId: string;
  sourceId: string;
  sourceType: string;
  format: ExportFormat;
  status: 'QUEUED' | 'STARTED' | 'FINISHED' | 'FAILED';
  createdAt?: string;
  startedAt?: string;
  finishedAt?: string;
  result?: {
    success?: boolean;
    message?: string;
    data?: {
      downloadUrl?: string;
      filename?: string;
      path?: string;
    } | null;
    error?: {
      code?: string;
      message?: string;
      details?: unknown;
    };
  };
};

export type ExportMetadata = {
  exportJob?: ExportJobStatus;
  existingExports: {
    key: string;
    signedUrl?: string;
    filename: string;
    createdAt?: string;
  }[];
};

export type InitiateTransactionExportParams = {
  format: ExportFormat;
  filters?: {
    startDate?: string;
    endDate?: string;
  };
  visibleFields?: string[];
};

export type Transaction = {
  action_id: string;
  transaction_id: string | null;
  target_field: string | null;
  amount: number;
  subledger: string | null;
  payment_provider: string;
  rc_status: string;
  transaction_state: string | null;
  transaction_clear_date: string | null;
  proposed_date: string | null;
  uid: string | null;
  entry_type: string;
  card_id: string | null;
  failure_message: string | null;
  trace_id: string | null;
  givecard_recipient_id: string | null;
  attempt: number;
};

type TransactionsContextType = {
  transactions: Transaction[];
  isLoading: boolean;
  error: string | null;
  fetchTransactions: (startDate?: string, endDate?: string) => Promise<void>;
  paymentProviderOptions: string[];
  // Export functionality
  initiateTransactionExport: (params: InitiateTransactionExportParams) => Promise<{ taskId: string; status: string } | void>;
  getTransactionExportStatus: (taskId?: string) => Promise<ExportMetadata | void>;
  exportJob: ExportJobStatus | undefined;
  isExporting: () => boolean;
};

const TransactionsContext = createContext<TransactionsContextType | undefined>(undefined);

export const useTransactionsContext = () => {
  const context = useContext(TransactionsContext);
  if (!context) {
    throw new Error('useTransactionsContext must be used within a TransactionsProvider');
  }
  return context;
};

type TransactionsProviderProps = {
  children: ReactNode;
};

export const TransactionsProvider: React.FC<TransactionsProviderProps> = ({ children }) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentProviderOptions, setPaymentProviderOptions] = useState<string[]>([]);
  const [exportJob, setExportJob] = useState<ExportJobStatus | undefined>(undefined);
  const exportPollingRef = useRef<NodeJS.Timeout | null>(null);

  const postTransactionDetails = usePost('/api/admin_panel/transaction_details');
  const postPaymentProviderOptions = usePost('/api/admin_panel/payment_provider_options');
  const postExportTransactions = usePost('/api/admin_panel/transactions/export');
  const postExportStatus = usePost('/api/admin_panel/transactions/export/status');

  const fetchTransactions = useCallback(async (startDate?: string, endDate?: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await postTransactionDetails({
        program: get_deployment(),
        startDate,
        endDate,
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      setTransactions(response.data?.transactions || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  }, [postTransactionDetails]);

  // This gets all payment provider options used in this program - we should always provide all of these
  // as an option in the filters.
  useEffect(() => {
    (async () => {
      try {
        const response = await postPaymentProviderOptions({});
        if (response.error) {
          setError(response.error.message);
        }
        setPaymentProviderOptions(response.data || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setPaymentProviderOptions([]);
      }
    })();
  }, []);

  // Export functionality
  const getTransactionExportStatus = useCallback(async (taskId?: string): Promise<ExportMetadata | void> => {
    try {
      const result = await postExportStatus({ taskId });
      if (result.success && result.data) {
        return result.data as ExportMetadata;
      } else if (result.error) {
        console.error('Error getting transaction export status:', result.error);
      }
    } catch (error) {
      console.error('Error getting transaction export status:', error);
    }
  }, [postExportStatus]);

  const startExportPolling = useCallback((taskId: string) => {
    // Clear any existing polling
    if (exportPollingRef.current) {
      clearInterval(exportPollingRef.current);
    }

    // Poll every 3 seconds
    const intervalId = setInterval(async () => {
      const metadata = await getTransactionExportStatus(taskId);

      if (metadata?.exportJob) {
        const job = metadata.exportJob;
        setExportJob(job);

        // Stop polling when job is complete or failed
        if (job.status === 'FINISHED' || job.status === 'FAILED') {
          if (exportPollingRef.current) {
            clearInterval(exportPollingRef.current);
            exportPollingRef.current = null;
          }
        }
      }
    }, 3000);

    exportPollingRef.current = intervalId;
  }, [getTransactionExportStatus]);

  const initiateTransactionExport = useCallback(async (params: InitiateTransactionExportParams): Promise<{ taskId: string; status: string } | void> => {
    try {
      const result = await postExportTransactions({
        format: params.format,
        filters: params.filters,
        visibleFields: params.visibleFields,
      });

      if (result.success && result.data) {
        const { taskId, status } = result.data;

        // Set initial job status
        setExportJob({
          taskId,
          sourceId: 'transactions',
          sourceType: 'transaction',
          format: params.format,
          status: status as ExportJobStatus['status'],
          createdAt: new Date().toISOString(),
        });

        // Start polling for status
        startExportPolling(taskId);

        return { taskId, status };
      } else if (result.error) {
        console.error('Error initiating transaction export:', result.error);
      }
    } catch (error) {
      console.error('Error initiating transaction export:', error);
    }
  }, [postExportTransactions, startExportPolling]);

  const isExporting = useCallback((): boolean => {
    return exportJob?.status === 'QUEUED' || exportJob?.status === 'STARTED';
  }, [exportJob]);

  // Cleanup export polling on unmount
  useEffect(() => {
    return () => {
      if (exportPollingRef.current) {
        clearInterval(exportPollingRef.current);
      }
    };
  }, []);

  const value: TransactionsContextType = {
    transactions,
    isLoading,
    error,
    fetchTransactions,
    paymentProviderOptions,
    // Export functionality
    initiateTransactionExport,
    getTransactionExportStatus,
    exportJob,
    isExporting,
  };

  return (
    <TransactionsContext.Provider value={value}>
      {children}
    </TransactionsContext.Provider>
  );
};
